import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { KnowledgeContentPreviewComponent } from './knowledge-content-preview/knowledge-content-preview.component';
import { KnowledgeContentEditor } from './knowledge-content-editor';
import { useKnowledgeItem } from 'shared/hooks/knowledge-items.hooks';
import { EVENTS, ENTITY_TYPES } from 'shared/constants';
import { useContentPreview } from 'src/pages/application/knowledge-content/hooks';
import { useLocation } from 'react-router-dom';
import analyticsService from '../../helpers/analytics.service';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { useDocumentEditor } from 'src/components/document-editor/hooks/use-document-editor';
import { getFileExtension } from 'shared/utils';

const KnowledgeContentComponent = React.memo(function KnowledgeContentComponent({
  type,
  close,
  id,
  ...props
}) {
  const location = useLocation();

  const isEditRoute = location.pathname.includes('/edit') || location.pathname.includes('/new');
  const hasInitialData = location.state?.initialData;
  const isEditMode = isEditRoute || hasInitialData;

  const lang = useSelector(getLang('KNOWLEDGE_EDITOR'));
  const langTagsForm = useSelector(getLang('TAGS_FORM'));
  const langTags = useSelector(getLang('TAGS'));
  const langRevision = useSelector(getLang('REVISION_LOG'));
  const [knowledgeId, setKnowledgeId] = useState(id);
  const analyticsTrackedRef = useRef(null);

  const initialData = location.state?.initialData;

  const knowledgeEntityForEditor = initialData
    ? {
        id: knowledgeId,
        ...initialData,
        title:
          initialData.title ||
          initialData.fileName?.slice(0, initialData.fileName.lastIndexOf('.')),
      }
    : null;

  const { isHub, isPublic, setItemId, item } = useContentPreview();
  // A hub item is cross-org and resolves through the hub context, not this org's items.
  const { data: knowledge } = useKnowledgeItem(isHub ? undefined : knowledgeId);
  const contextId = searchContextIdManager.getContextId();

  // Get document editor support info for analytics
  const knowledgeItem = isHub ? item : knowledge;
  const { canRenderInEditor } = useDocumentEditor(knowledgeItem?.link, knowledgeItem?.type);
  const originalFormat = getFileExtension(knowledgeItem?.link) || null;

  useEffect(() => {
    if (isHub) {
      setItemId(id);
    }
  }, [id, isHub, setItemId]);

  useEffect(() => {
    if (isHub) {
      if (!item?.id) {
        return;
      }
    } else {
      if (!knowledge?.id || !knowledge.title) {
        return;
      }
    }

    if (isEditMode) return;

    // Create a unique key for this item and context
    const trackingKey = `${knowledgeId}-${isHub ? 'hub' : 'regular'}-${location.pathname}`;

    // Check if we've already tracked this specific item
    if (analyticsTrackedRef.current === trackingKey) {
      return;
    }

    const knowledgeItemForEvent = isHub ? item : knowledge;

    const eventData = {
      id: knowledgeItemForEvent.id.toString(),
      title: knowledgeItemForEvent.title,
      ownerIds: knowledgeItemForEvent.ownerIds,
      type: knowledgeItemForEvent.type,
      ...knowledgeItemForEvent.tags,
      is_new_editor: canRenderInEditor,
      original_format: originalFormat,
    };

    if (isHub) {
      analyticsService.track(EVENTS.KNOWLEDGE_HUB.OPEN_ITEM, {
        ...eventData,
        isPublic,
      });
    } else {
      analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.OPEN, {
        ...eventData,
        contextId,
      });
    }

    analyticsService.track(EVENTS.CONTENT.OPEN, {
      ...eventData,
      entityType: ENTITY_TYPES.knowledge,
      contextId,
    });

    // Mark this item as tracked
    analyticsTrackedRef.current = trackingKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeId, knowledge?.id, item?.id, isEditMode, isHub, location.pathname]);

  const closeEditMode = useCallback(
    id => {
      if (!id) {
        // No ID means we're closing without saving - go back to knowledge base
        close();
        return;
      }

      if (isEditMode) {
        // In edit mode with an ID means we saved successfully
        // Let the editor's navigation to item view take precedence
        // Don't call close() here to avoid double navigation
        return;
      }

      setKnowledgeId(id);
    },
    [setKnowledgeId, close, isEditMode],
  );

  if (isHub && knowledgeEntityForEditor) {
    return (
      <KnowledgeContentEditor
        knowledge={{
          ...knowledgeEntityForEditor,
        }}
        onFinish={closeEditMode}
        {...props}
      />
    );
  }

  if (isEditMode) {
    const subtitle =
      knowledge?.subtitle || knowledgeEntityForEditor?.subtitle || props?.subtitle || '';
    return (
      <KnowledgeContentEditor
        knowledge={{
          type,
          id: knowledgeId,
          ...knowledge,
          ...props,
          ...knowledgeEntityForEditor,
          subtitle,
        }}
        onFinish={closeEditMode}
        categoryId={knowledgeEntityForEditor?.categoryId}
        {...props}
      />
    );
  }

  return (
    <KnowledgeContentPreviewComponent
      knowledge={knowledge}
      lang={lang}
      langTags={langTags}
      langTagsForm={langTagsForm}
      langRevision={langRevision}
      id={knowledgeId}
      close={close}
      {...props}
    />
  );
});

export { KnowledgeContentComponent };
